import { decodeEventLog, getAddress, type Address, type Hash } from "viem";
import { baseClient } from "./chain";
import { burnerAbi } from "./contracts";

export type PendingRedemption = {
  transactionHash: Hash;
  logIndex: number;
  redemptionId: Hash;
};

export type Redemption = {
  sourceChainId: bigint;
  sourceContract: Address;
  redemptionId: Hash;
  redemptionNumber: bigint;
  burner: Address;
  recipient: Address;
  burnAmount: bigint;
};

export async function recoverRedemption(
  transactionHash: Hash,
  burnerAddress: Address,
  requestedLogIndex?: number,
) {
  const [receipt, finalizedBlock] = await Promise.all([
    baseClient.getTransactionReceipt({ hash: transactionHash }),
    baseClient.getBlock({ blockTag: "finalized" }),
  ]);
  if (receipt.status !== "success") throw new Error("That Base transaction did not succeed.");
  if (receipt.blockNumber > finalizedBlock.number) {
    throw new Error("That Base burn is confirmed but not finalized yet. Wait a moment and try again.");
  }

  for (const log of receipt.logs) {
    if (log.address.toLowerCase() !== burnerAddress.toLowerCase()) continue;
    if (requestedLogIndex !== undefined && log.logIndex !== requestedLogIndex) continue;
    try {
      const decoded = decodeEventLog({ abi: burnerAbi, data: log.data, topics: log.topics });
      if (decoded.eventName !== "RedemptionInitiated") continue;
      const args = decoded.args;
      const redemption: Redemption = {
        sourceChainId: 8453n,
        sourceContract: burnerAddress,
        redemptionId: args.redemptionId,
        redemptionNumber: args.redemptionNumber,
        burner: getAddress(args.burner),
        recipient: getAddress(args.recipient),
        burnAmount: args.burnAmount,
      };
      return {
        pending: {
          transactionHash,
          logIndex: log.logIndex,
          redemptionId: args.redemptionId,
        } satisfies PendingRedemption,
        redemption,
        blockNumber: receipt.blockNumber,
      };
    } catch {
      // Continue until a matching redemption event is found.
    }
  }
  throw new Error("No Hardware redemption event was found in that Base transaction.");
}
